import { isAxiosError } from "axios";
import { useCallback, useRef, useState } from "react";
import type { PlannedMutation } from "@/shared/services/resourceUsageService";
import { previewResourceUsage } from "@/shared/services/resourceUsageService";
import type { ImpactReport, RequiresConfirmationResponse } from "@/shared/types/resourceUsage.types";

const EMPTY_REPORT: ImpactReport = { features: [] };

/**
 * ADM-856: extracts the impact report from a backend 409 "requires_confirmation"
 * response, or null for any other error. Lets a mutation's onError skip its toast
 * and open the confirmation dialog instead.
 */
export function getRequiresConfirmationReport(error: unknown): ImpactReport | null {
  if (!isAxiosError(error) || error.response?.status !== 409) return null;
  const data = error.response.data as RequiresConfirmationResponse | undefined;
  return data?.error === "requires_confirmation" ? data.impact_report : null;
}

// report is null while the preview that fills the impacted-groups list is pending.
type PendingConfirmation = { report: ImpactReport | null, proceed: () => Promise<void> };

/**
 * Coordinates the confirmation dialog. Three entry points:
 *  - guard(run, vars): apply-and-catch for a SINGLE operation — runs it, and on a
 *    409 opens the dialog whose confirm replays it with `confirmed: true`.
 *  - requireConfirmation(report, onConfirm): preview-driven for a BATCH — the caller
 *    already fetched the impact report (nothing applied yet); the dialog's confirm
 *    runs `onConfirm`, which applies the whole batch atomically.
 *  - confirmInUse(mutations, onConfirm): in_use-driven (ADM-856). The `in_use` flag
 *    already told us a confirmation is needed, so the dialog opens INSTANTLY (no
 *    round-trip); the preview only fills the impacted-groups list, shown as skeletons
 *    until it resolves. The mutation is held until the user confirms.
 */
export function useResourceUsageConfirmation() {
  const [pending, setPending] = useState<PendingConfirmation | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  // Identifies the confirmation a preview belongs to. Cancelling and immediately
  // starting another one leaves the first preview in flight; without this its late
  // response would land in the new dialog and describe the wrong mutation.
  const previewGeneration = useRef(0);

  const proceedFor = useCallback((action: () => Promise<unknown>) => async () => {
    setIsConfirming(true);
    try {
      await action();
    }
    // A replay can still fail (network, 422, a refusal the caller re-threw). Its own
    // onError has already reported it; swallowing here keeps the click handlers from
    // leaking an unhandled rejection. Closing in `finally` rather than after `await`
    // is what stops a failed replay from stranding the user on a dialog whose only
    // button just failed.
    catch { /* reported by the mutation that ran it */ }
    finally {
      setPending(null);
      setIsConfirming(false);
    }
  }, []);

  // Every transition that replaces or clears the dialog claims a new generation, so any
  // preview still in flight for the previous one is recognised as stale on arrival.
  const claimGeneration = useCallback(() => {
    previewGeneration.current += 1;
    return previewGeneration.current;
  }, []);

  const open = useCallback((report: ImpactReport, action: () => Promise<unknown>) => {
    claimGeneration();
    setPending({ report, proceed: proceedFor(action) });
  }, [claimGeneration, proceedFor]);

  const confirmInUse = useCallback((mutations: PlannedMutation[], onConfirm: () => Promise<unknown>) => {
    const generation = claimGeneration();
    const isCurrent = () => generation === previewGeneration.current;
    const applyIfCurrent = (report: ImpactReport) =>
      setPending((current) => (current && isCurrent() ? { ...current, report } : current));

    setPending({ report: null, proceed: proceedFor(onConfirm) });
    setIsPreviewing(true);
    previewResourceUsage(mutations)
      .then(applyIfCurrent)
      .catch(() => applyIfCurrent(EMPTY_REPORT))
      .finally(() => {
        if (isCurrent()) setIsPreviewing(false);
      });
  }, [claimGeneration, proceedFor]);

  const guard = useCallback(
    async <V extends { confirmed?: boolean }>(
      run: (vars: V) => Promise<unknown>,
      vars: V,
    ): Promise<"applied" | "pending"> => {
      try {
        await run(vars);
        return "applied";
      }
      catch (error) {
        const report = getRequiresConfirmationReport(error);
        if (!report) throw error;

        open(report, () => run({ ...vars, confirmed: true }));
        return "pending";
      }
    },
    [open],
  );

  const requireConfirmation = useCallback(
    (report: ImpactReport, onConfirm: () => Promise<unknown>) => open(report, onConfirm),
    [open],
  );

  const cancel = useCallback(() => {
    claimGeneration();
    setPending(null);
    setIsPreviewing(false);
  }, [claimGeneration]);

  return {
    pending, isConfirming, isPreviewing, guard, requireConfirmation, confirmInUse, cancel,
  };
}
